type FormatProps = {
  formatCurrency: (amount: number) => string
  removeNewLines: (text: string) => string
  formatDocument: (document: string) => string
  formatCEP: (cep: string) => string
  formatPhone: (phone: string) => string
  validateCPF: (cpf: string) => boolean
  validateCNPJ: (cpf: string) => boolean
}

export const useFormat = (): FormatProps => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat(
      'pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(amount)
  }

  const removeNewLines = (text: string): string => {
    return text.replace(/(\r\n|\n|\r)/gm, ' ')
  }

  const validateCPF = (cpf: string): boolean => {
    const cpfNumeros = cpf.replace(/\D/g, '')

    if (cpfNumeros.length !== 11) {
      return false
    }

    if (/^(\d)\1{10}$/.test(cpfNumeros)) {
      return false
    }

    let soma = 0
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpfNumeros[i]) * (10 - i)
    }

    let resto = soma % 11
    const digito1 = resto < 2 ? 0 : 11 - resto

    soma = 0
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpfNumeros[i]) * (11 - i)
    }

    resto = soma % 11
    const digito2 = resto < 2 ? 0 : 11 - resto

    if (parseInt(cpfNumeros[9]) !== digito1 || parseInt(cpfNumeros[10]) !== digito2) {
      return false
    }

    return true
  }

  const validateCNPJ = (cnpj: string): boolean => {
    const clean = cnpj.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()

    if (clean.length !== 14) {
      return false
    }

    if (/^(.)\1{13}$/.test(clean)) {
      return false
    }

    const hasLetters = /[A-Z]/.test(clean)

    if (hasLetters && !/\d{2}$/.test(clean)) {
      return false
    }

    const getCharValue = (char: string): number => {
      return char.charCodeAt(0) - 48
    }

    let size = 12
    let numbers = clean.substring(0, size)
    const digits = clean.substring(size)

    let soma = 0
    let peso = 2

    for (let i = size - 1; i >= 0; i--) {
      const charValue = getCharValue(numbers.charAt(i))
      soma += charValue * peso
      peso = peso === 9 ? 2 : peso + 1
    }

    let resto = soma % 11
    const digito1 = resto < 2 ? 0 : 11 - resto

    if (digito1 !== parseInt(digits.charAt(0))) {
      return false
    }

    size = 13
    numbers = clean.substring(0, size)

    soma = 0
    peso = 2

    for (let i = size - 1; i >= 0; i--) {
      const charValue = getCharValue(numbers.charAt(i))
      soma += charValue * peso
      peso = peso === 9 ? 2 : peso + 1
    }

    resto = soma % 11
    const digito2 = resto < 2 ? 0 : 11 - resto

    if (digito2 !== parseInt(digits.charAt(1))) {
      return false
    }

    return true
  }

  const formatDocument = (document: string): string => {
    const hasLetters = /[a-zA-Z]/.test(document)
    const cleanValue = hasLetters
      ? document.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
      : document.replace(/\D/g, '')

    if (cleanValue.length <= 11) {
      return cleanValue
        .replace(/([A-Z0-9]{3})([A-Z0-9])/, '$1.$2')
        .replace(/([A-Z0-9]{3})([A-Z0-9])/, '$1.$2')
        .replace(/([A-Z0-9]{3})([A-Z0-9]{1,2})$/, '$1-$2')
    } else if (cleanValue.length <= 14) {
      return cleanValue
        .replace(/([A-Z0-9]{2})([A-Z0-9])/, '$1.$2')
        .replace(/([A-Z0-9]{3})([A-Z0-9])/, '$1.$2')
        .replace(/([A-Z0-9]{3})([A-Z0-9])/, '$1/$2')
        .replace(/([A-Z0-9]{4})([A-Z0-9]{1,2})$/, '$1-$2')
    }

    return cleanValue
  }

  const formatCEP = (cep: string): string => {
    const numericValue = cep.replace(/\D/g, '')
    return numericValue.replace(/^(\d{5})(\d{1,3})?$/, '$1-$2').substring(0, 9)
  }

  const formatPhone = (phone: string): string => {
    const numericValue = phone.replace(/\D/g, '')
    if (numericValue.length <= 10) {
      return numericValue
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .substring(0, 14)
    }
    return numericValue
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .substring(0, 15)
  }

  return {
    formatCurrency,
    formatDocument,
    formatCEP,
    formatPhone,
    removeNewLines,
    validateCPF,
    validateCNPJ
  }
}
